<script setup lang="ts">
import { CategoryOption, SubjectSummary } from "@/presentlevels/presentlevels_types.ts";
import { Standard } from '@/standards/standard_types';
import { PropType, Ref, ref, watch, onBeforeUnmount, computed, onMounted, nextTick, provide, ComputedRef } from 'vue'
import PresentlevelsSkillsModal from "@/presentlevels/PresentlevelsSkillsModal.vue";
import { usePresentLevelsStore } from '@/presentlevels/presentlevels.store'
import WikiTextEditor from "@/components/editor/WikiTextEditor.vue";
import SaveToLibrary from '@/library/SaveToLibrary.vue'
import GbButton from '@/components/GbButton.vue'
import { transformResourceComponentsToWiki, transformWikiToResourceComponents } from '@/components/editor/wiki_tiptap_helpers'
import { getEditorPlainText, processStatementText } from './copy-statement-helpers'
import type { ImpactStatement } from './presentlevels_types'
import SubjectSummaryLoading from '@/presentlevels/SubjectSummaryLoading.vue'
import GbDialog from '@/components/GbDialog.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/tooltip'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { PRESENTLEVELS_EVENT_NAMES } from '@/presentlevels/event_names.ts'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import { PL_SUMMARY_SAVING_STATES } from '@/presentlevels/presentlevels_constants'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import StandardPopover from '@/standards/StandardPopover.vue'
import { headingsForStandardSetCode } from '@/standards/standard_headings.ts'
import { useLibraryStore } from '@/library/library.store.ts'
import { LibraryFolder } from '@/library/library_types.ts'
import TextLinkButton from '@/components/TextLinkButton.vue'
import CopySummaryModal from '@/presentlevels/CopySummaryModal.vue'
import { isFolderShared } from '@/library/library_helpers.ts'
import FolderIcon from '@/library/FolderIcon.vue'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable.js'
import { toolkitUrls } from '@/util/links.js'
import { useCopyText } from '@/util/use_copy_text'
import AssessmentUploadModal from '@/presentlevels/AssessmentUploadModal.vue'
import DropdownButton from '@/components/DropdownButton.vue'
import PresentlevelsImpactStatement from '@/presentlevels/PresentlevelsImpactStatement.vue'
import { useVisibilityStore } from '@/visibility/visibility.store'

const presentLevelsStore = usePresentLevelsStore()
const libraryStore = useLibraryStore()
const contentProfileStore = useContentProfileStore()
const visibilityStore = useVisibilityStore()

provide('subjectLabel', computed(() => props.summary?.subject_label || ''))
const toEmbeddedText = transformWikiToResourceComponents
const toWikiText = transformResourceComponentsToWiki
const standards = ref([] as Standard[])

const groupedStandards = computed(() => {
  const groups: { [key: string]: Standard[] } = {}
  for (const standard of standards.value) {
    const setCode = standard.standard_set_code
    if (!groups[setCode]) {
      groups[setCode] = []
    }
    groups[setCode].push(standard)
  }
  return groups
})

const emit = defineEmits<{
  'text-changed': [text: string]
  'impact-statements-changed': [statements: ImpactStatement[]]
  'copied': []
  'collapsed': [isCollapsed: boolean]
}>()

const props = defineProps({
  summary: {
    type: Object as PropType<SubjectSummary>,
    required: true
  },
  categoryOptions: {
    type: Array as PropType<Array<CategoryOption>>,
    default: () => []
  },
  savedSummary: {
    type: Boolean,
    default: false
  },
})

const analyticsTracker = getAnalyticsTracker().getChildContext({
  plw_statement_subject: props.summary?.subject_label
})

const placeHolder = computed(() => {
  // For saved summaries without AI features, show read-only placeholder
  if (props.savedSummary && !visibilityStore.aiFeatureVisibility) {
    return 'Saved statement (AI features disabled)'
  }
  if (props.summary?.subject_label === 'Reading' && visibilityStore.reportUploadVisibility) {
    return 'Upload a score report or select a category below to begin drafting statement'
  }
  return 'Select a category below to begin drafting statement'
})
const statementText = ref(props.summary?.statement_text)

// Initialize impact statements from props
const impactStatements = ref<ImpactStatement[]>(props.summary?.impact_statements || [])
const isSyncingFromProps = ref(false)

const saveStatus = computed(() => {
  if (!props.summary?.uuid) return PL_SUMMARY_SAVING_STATES.NO_CHANGES
  return presentLevelsStore.savingStates[props.summary.uuid] || PL_SUMMARY_SAVING_STATES.NO_CHANGES
})

const libraryFolder: ComputedRef<LibraryFolder | undefined> = computed(() => {
  const uuid = props.summary?.uuid
  return uuid ? libraryStore.getFolderByContentUuid(uuid) : undefined
})

const userRole = computed(() => {
  if (!libraryFolder.value) {
    return undefined
  }
  return libraryStore.getUserRoleForFolder(libraryFolder.value?.uuid)
})
const isContributor = computed(() => userRole.value === 'contributor')

const getStandards = (): void => {

  const standardsForSummary = props.summary?.standards ?? []
  if (!standardsForSummary.length) {
    return
  }
  const standardSetsForProfile: string[] = contentProfileStore.visibleStandardSets ?? []
  const relevantStandards: Standard[] = standardsForSummary.filter((s: Standard) => standardSetsForProfile.includes(s.standard_set_code))
  const allStandards = [...standards.value, ...relevantStandards];
  standards.value = Array.from(new Map(allStandards.map((s: Standard) => [s.standard_long_ref, s])).values());
}

onMounted(() => {
  getStandards()
})

watch(() => props.summary?.standards, () => {
  getStandards()
}, { deep: true })

watch(() => contentProfileStore.activeProfile, () => {
  standards.value = []
  getStandards()
})

watch(() => props.summary?.statement_text, (newStatementText) => {
  if (newStatementText !== statementText.value) {
    isSyncingFromProps.value = true
    statementText.value = newStatementText
    nextTick().then(() => {
      isSyncingFromProps.value = false
    })
  }
})

watch(() => props.summary?.impact_statements, (newImpactStatements) => {
  if (JSON.stringify(newImpactStatements) !== JSON.stringify(impactStatements.value)) {
    isSyncingFromProps.value = true
    impactStatements.value = newImpactStatements ? JSON.parse(JSON.stringify(newImpactStatements)) : []
    nextTick().then(() => {
      isSyncingFromProps.value = false
    })
  }
}, { deep: true })

watch(statementText, () => {
  // Don't emit if we're syncing from props (to avoid triggering unsaved changes during reset)
  if (!isSyncingFromProps.value) {
    emit('text-changed', statementText.value || '')

    if (!props.savedSummary) {
      debouncedSaveStatementText()
    }
  }
})

watch(impactStatements, () => {
  // Don't emit if we're syncing from props (to avoid infinite loop)
  if (!isSyncingFromProps.value) {
    emit('impact-statements-changed', impactStatements.value)
    
    if (!props.savedSummary) {
      debouncedSaveStatementText()
    }
  }
}, { deep: true })

const saveStatementText = async (): Promise<void> => {
  if (!props.summary?.uuid) return

  try {
    await presentLevelsStore.saveSubjectSummary(
      props.summary.uuid,
      {
        statement_text: statementText.value || '',
        aligned_goals: props.summary?.aligned_goals,
        impact_statements: impactStatements.value
      }
    )
  } catch (error) {
    console.error('Failed to save statement text:', error)
  }
}

const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
const statusResetTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
const debouncedSaveStatementText = () => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value)
  }

  timeoutId.value = setTimeout(() => {
    void saveStatementText()
  }, 300)
}

function discardTextEdits(statements: ImpactStatement[], text?: string) {
  isSyncingFromProps.value = true
  impactStatements.value = statements ? JSON.parse(JSON.stringify(statements)) : []
  if (text !== undefined) {
    statementText.value = text
  }
  nextTick().then(() => {
    isSyncingFromProps.value = false
  })
}

defineExpose({
  saveStatementText,
  discardTextEdits
})

function handleEditModeUpdated(newIsEditMode: boolean) {
  if (!props.savedSummary && !newIsEditMode) {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
    }
  }
}

const impactStatementCopyTitle = computed(() => {
  if (!impactStatements.value || impactStatements.value.length === 0) {
    return ''
  }
  
  // Build title from all impact statements: "Designation: Impact Area 1 | Impact Area 2"
  const titles = impactStatements.value.map(statement => {
    const areas = statement.impact_area_labels.join(' | ')
    return `${statement.designation_label}: ${areas}`
  })
  
  // Join multiple statements with semicolon
  return titles.join('; ')
})

const selectedCategory: Ref<CategoryOption | null> = ref(null)
function setSelectedCategory(category: CategoryOption | null) {
  selectedCategory.value = category

  if (category) {
    analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.CLICKED_CATEGORY, {plw_statement_category: category.label})
  }
}

async function deleteStatement() {
  if (props.summary?.uuid) {
    await presentLevelsStore.deleteStatement(props.summary.uuid)
    analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.DELETED_AREA)
  }
}
const showDeleteModal = ref(false)
const showCopySummaryModal = ref(false)
const clearModalType = ref<'strengths' | 'impact' | null>(null)
const impactStatementIndexToRemove = ref<number | null>(null)
const savedFolderError = ref(false)

const handleSaveToFolder = async () => {
  savedFolderError.value = false
  if (props.summary?.uuid) {
    try {
      await libraryStore.fetchFoldersForPLSubjectSummaries()
    } catch (error) {
      console.error('Failed to refresh library state after save:', error)
    }
  }
}
function handleSaveToFolderError(error: unknown) {
  savedFolderError.value = true
  console.error('Save to library failed:', error)
}

const editorWrapperClasses: ComputedRef<string> = computed(() => {
  const classes = ['rounded-md']
  if (isContributor.value) {
    classes.push('bg-gray-200')
    classes.push('py-1')
  } else if (props.summary?.subject_label === 'Reading') {
    classes.push('bg-blue-50')
    classes.push('overflow-hidden')
  } else {
    classes.push('bg-white')
    classes.push('py-2')
  }
  return classes.join(' ')
})

const readOnlyMessageClasses = 'py-2 px-1.5 bg-gray-1000 text-white font-default text-sm font-regular leading-[1.125rem] mt-1 flex items-center gap-1'

const isBehaviorSubject = computed(() => props.summary?.subject_label === 'Behavior')
const browseResourcesSubject = computed(() => {
  if (!isBehaviorSubject.value) return ''
  return getGoalSubjectLabelFromValue('Behavior & SEL')
})

function handleBehaviorLinkClick() {
  analyticsTracker.sendEvent(PRESENTLEVELS_EVENT_NAMES.RESOURCES.BROWSE_BEHAVIOR_DATA_COLLECTION)
}

function handleAddImpactStatement(statement: ImpactStatement) {
  impactStatements.value.push(statement)
}

function handleRemoveImpactStatement(index: number) {
  impactStatementIndexToRemove.value = index
  clearModalType.value = 'impact'
}

function handleUpdateImpactStatement(index: number, statementText: string) {
  if (impactStatements.value[index]) {
    impactStatements.value[index].statement_text = statementText
  }
}

const isCollapsed = ref(false)
function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
  emit('collapsed', isCollapsed.value)
}

const { copyText, isTooltipOpen } = useCopyText()
const { copyText: copyAllText, isTooltipOpen: isAllStatementsTooltipOpen } = useCopyText()
const wikiTextEditorRef = ref<InstanceType<typeof WikiTextEditor> | null>(null)

const showAssessmentModal = ref(false)
const selectedScoreReportSeries = ref('')
const selectedScoreReportTitle = ref('')

function handleUploadScoreReportClick(series: string, title: string) {
  analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SCORE_REPORT.CLICKED_UPLOAD, {
    score_report_series: series,
    score_report_title: title
  })
  selectedScoreReportSeries.value = series
  selectedScoreReportTitle.value = title
  showAssessmentModal.value = true
}

async function handleStatementInserted(text: string) {
  if (props.summary?.uuid) {
    try {
      // Append the new statement to the existing statement text
      const existingText = statementText.value || ''
      const combinedText = existingText ? `${existingText}<p></p>${text}` : text
      
      await presentLevelsStore.saveSubjectSummary(
        props.summary.uuid,
        {
          statement_text: combinedText,
          aligned_goals: props.summary?.aligned_goals,
          impact_statements: impactStatements.value
        }
      )
    } catch (error) {
      console.error('Failed to save statement from assessment upload:', error)
    }
  }
}

async function copyAllStatements(event: MouseEvent) {
  const statements: string[] = []
  
  // Get strengths/needs statement text
  // Use the underlying data instead of editor instance to work when collapsed
  if (statementText.value) {
    const processedText = processStatementText(statementText.value)
    if (processedText.trim()) {
      statements.push(processedText)
    }
  }
  
  // Get all impact statements
  if (impactStatements.value && impactStatements.value.length > 0) {
    impactStatements.value.forEach(statement => {
      const processedText = processStatementText(statement.statement_text)
      if (processedText.trim()) {
        statements.push(processedText)
      }
    })
  }
  
  if (statements.length > 0) {
    const combinedText = statements.join('\n\n')
    try {
      await copyAllText(combinedText, event, { autoHideTooltip: true, autoHideDelay: 3000 })
    } catch (error) {
      console.error('Failed to copy all statements:', error)
    }
  }
}

async function copyStatementText(event: MouseEvent) {
  const processedText = getEditorPlainText(wikiTextEditorRef.value)
  if (processedText) {
    try {
      await copyText(processedText, event, { autoHideTooltip: true, autoHideDelay: 3000 })
    } catch (error) {
      console.error('Failed to copy statement text:', error)
      // Show error message via tooltip by setting isTooltipOpen
      // The tooltip will show the error state briefly
      isTooltipOpen.value = true
      setTimeout(() => {
        isTooltipOpen.value = false
      }, 3000)
    }
  }
}

function clearStrengthsNeeds() {
  clearModalType.value = 'strengths'
}

function confirmClear() {
  if (clearModalType.value === 'strengths') {
    analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.CLEARED_STATEMENT, { statement_type: 'strength_need_statement' })
    statementText.value = ''
  } else if (clearModalType.value === 'impact' && impactStatementIndexToRemove.value !== null) {
    analyticsTracker.sendEvent(EVENT_NAMES.PRESENTLEVELS.SUMMARY.CLEARED_STATEMENT, { statement_type: 'impact_statement' })
    impactStatements.value.splice(impactStatementIndexToRemove.value, 1)
    impactStatementIndexToRemove.value = null
  }
  clearModalType.value = null
}

const clearModalConfig = computed(() => {
  if (clearModalType.value === 'strengths') {
    return {
      header: 'Clear Strengths & Needs Statement?',
      body: `You have created a <span class="font-bold">Strengths & Needs Statement in ${props.summary?.subject_label}</span>. If you proceed, all content in this statement will be lost. Are you sure you want to clear this statement?`
    }
  } else if (clearModalType.value === 'impact') {
    return {
      header: 'Clear Impact Statement?',
      body: `You have created an <span class="font-bold">Impact Statement in ${props.summary?.subject_label}</span>. If you proceed, all content in this statement will be lost. Are you sure you want to clear this statement?`
    }
  }
  return { header: '', body: '' }
})

onBeforeUnmount(() => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value)
  }
  if (statusResetTimeoutId.value) {
    clearTimeout(statusResetTimeoutId.value)
  }
})
</script>

<template>
  <div class="md:w-plw-subject-summary rounded-lg border border-[#CCC] shadow-[0_4px_6px_-4px_rgba(0,0,0,0.1),0_10px_15px_-3px_rgba(0,0,0,0.1)] overflow-hidden" :class="isCollapsed ? 'max-h-[60px] mb-2' : 'mb-3'">
    <div v-if="props.categoryOptions?.length">
      <div 
        role="button"
        tabindex="0"
        :aria-expanded="!isCollapsed"
        :aria-controls="`subject-summary-content-${props.summary?.uuid}`"
        class="bg-blue-200 hover:bg-[#CEF] px-5 py-3 flex items-center justify-between cursor-pointer transition-colors"
        @click="toggleCollapse"
        @keydown.enter="toggleCollapse"
        @keydown.space.prevent="toggleCollapse"
      >
          <div class="flex items-center gap-2">
            <span 
              class="material-icons-round text-2xl transition-transform duration-200"
              :class="{ '-rotate-90': isCollapsed }"
            >
              expand_more
            </span>
            <span class="text-xl font-bold">{{ props.summary?.subject_label?.toUpperCase() }}</span>
            <CopyTrackWrapper
              type="presentlevelsubjectsummary"
            >
              <div class="relative">
                <TooltipProvider :delayDuration="0">
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <button
                        @click.stop="copyAllStatements"
                        type="button"
                        class="flex items-center justify-center p-2.5 hover:bg-gray-200 rounded-full transition-colors min-w-[44px] min-h-[44px]"
                        aria-label="Copy all statements"
                      >
                        <span class="material-icons-outlined text-xl">content_copy</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" v-if="!isAllStatementsTooltipOpen">
                      <p>Copy {{ props.summary?.subject_label }} statements</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider :delayDuration="0">
                  <Tooltip :open="isAllStatementsTooltipOpen">
                    <TooltipTrigger as-child>
                      <div class="absolute inset-0 pointer-events-none"></div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>Statements copied!</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CopyTrackWrapper>
            <a
              v-if="isBehaviorSubject"
              :href="`${toolkitUrls.browseAssessments}?subject=${encodeURIComponent(browseResourcesSubject)}&category=Behavior%20Data%20Collection`"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-400 hover:text-blue-500 text-md font-normal leading-4"
              @click.stop="handleBehaviorLinkClick"
            >
              <span class="inline-flex items-baseline gap-0.5"><span class="hover:underline">Browse data collection resources</span><span class="material-icons text-sm translate-y-0.5">open_in_new</span></span>
            </a>
          </div>
          <div v-if="!savedSummary && !isContributor" class="flex items-center gap-4" @click.stop>
            <div v-if="libraryFolder?.name" class="text-md flex items-center gap-1 text-green-300" id="saved-to-library-label">
              <span>Saved to <router-link
                id="saved-folder-link"
                :to="{ name: 'LibraryFolder', params: { uuid: libraryFolder.uuid }, query: { ref: 'plw_folder_link' } }"
                target="_blank"
                rel="noopener"
                :aria-label="`Open ${libraryFolder.name} folder in a new tab`"
                class="border-b border-green-300 items-center hover:text-green-400 transition-colors"
              >
                <FolderIcon :isShared="!!libraryFolder && isFolderShared(libraryFolder)" color-class="text-green-300" />
                <span class="fs-exclude">{{ libraryFolder.name }}</span>
                <span class="sr-only">(opens in a new tab)</span>
              </router-link></span>
            </div>
            <template v-else>
              <SaveToLibrary
                :content-uuid="props.summary?.uuid"
                content-type="pl_student_summary"
                content-type-label="Student Summary"
                size="xs"
                class="[&_button]:!px-[63px] [&_button]:!py-2"
                @save="handleSaveToFolder"
                @saveerror="handleSaveToFolderError"
              >
                <template #icon>
                  <span class="material-icons-round text-lg">folder</span>
                </template>
                <template #text>
                  Save
                </template>
              </SaveToLibrary>
              <div v-if="savedFolderError" class="text-md flex items-center text-red-300" id="save-error-label"><span class="material-icons-round">warning_amber</span><span>Save failed.</span></div>
            </template>
            <TooltipProvider :delayDuration="0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    class="flex items-center justify-center p-2.5 rounded-full hover:bg-gray-200 transition-colors min-w-[44px] min-h-[44px]"
                    @click="showDeleteModal = true"
                    aria-label="Delete statement"
                  >
                    <span class="material-icons-outlined text-[16px]">delete_outline</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent class="max-w-[300px]">
                  <p>Delete statement</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
        </div>
      </div>

      <div 
        v-if="!isCollapsed" 
        :id="`subject-summary-content-${props.summary?.uuid}`"
        class="bg-white"
      >
          <template v-if="isContributor">
            <p v-if="savedSummary" :class="readOnlyMessageClasses">
              <span class="material-icons text-lg" aria-hidden="true">lock</span>
              To make edits, copy this statement into a folder in your own library.
            </p>
            <div v-else :class="`${readOnlyMessageClasses} flex flex-nowrap items-center gap-1`">
              <span class="material-icons text-lg">lock</span>
              Only the owner of this Shared Folder can make edits. To make edits,
              <TextLinkButton
                text="copy to your Library"
                text-size="sm"
                icon-name="content_copy"
                icon-style="outlined"
                color="white"
                :default-underlined="true"
                @click="showCopySummaryModal = true"
              />
            </div>
          </template>

          <CopyTrackWrapper type="strength_need_statement">
            <div class="px-5 pt-3 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="text-md font-bold">Strengths & Needs</span>
                <div v-if="statementText" class="relative">
                  <TooltipProvider :delayDuration="0">
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <button
                          @click="copyStatementText"
                          class="flex items-center justify-center p-1 hover:bg-gray-200 rounded-full transition-colors"
                          aria-label="Copy statement text"
                        >
                          <span class="material-icons-outlined text-xl">content_copy</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" v-if="!isTooltipOpen">
                        <p>Copy strengths & needs statement</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider :delayDuration="0">
                    <Tooltip :open="isTooltipOpen">
                      <TooltipTrigger as-child>
                        <div class="absolute inset-0 pointer-events-none"></div>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>Statement copied!</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <TooltipProvider v-if="statementText" :delayDuration="0">
                <Tooltip>
                  <TooltipTrigger as-child>
                    <button
                      @click="clearStrengthsNeeds"
                      class="text-blue-400 hover:text-blue-500 text-sm font-normal transition-colors"
                      type="button"
                    >
                      Clear
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>Clear strengths & needs statement</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <WikiTextEditor
              ref="wikiTextEditorRef"
              v-model:wikiText="statementText"
              :toEmbeddedText="toEmbeddedText"
              :toWikiText="toWikiText"
              :outer-class="props.summary?.subject_label === 'Reading' ? 'px-5 pb-3 reading-plw-editor' : 'px-5 pb-3'"
              :editor-wrapper-class="editorWrapperClasses"
              :placeHolder="placeHolder"
              :reset-value="props.summary?.statement_text"
              edited-text-type="strength_need_statement"
              :read-only="isContributor"
              :hide-controls="true"
              @editModeUpdated="handleEditModeUpdated"
            >
              <template v-if="!isContributor" #innerContent>
                <!-- Upload Score Report footer for Reading -->
                <div
                  v-if="props.summary?.subject_label === 'Reading' && visibilityStore.reportUploadVisibility && !(props.savedSummary && !visibilityStore.aiFeatureVisibility)"
                  class="flex items-center gap-3 px-3 py-3 border-t border-[#b8d4e8] bg-[#d6eaf5] rounded-b-lg mx-0.5"
                >
                  <DropdownButton
                    dropdown-id="upload-assessment-menu"
                    button-theme="grayOutlineSecondary"
                    button-aria-label="Upload Score Report"
                    button-size="small"
                    additional-classes="!rounded-full !w-[44px] !h-[44px] !p-0 flex-shrink-0 !bg-white !flex !items-center !justify-center hover:!bg-gray-300"
                    menu-content-classes="!min-w-[240px]"
                    menu-label="Upload Score Report"
                    :items="[
                      { label: 'STAR Diagnostic', subtitle: 'Early Literacy or Reading', icon: 'file_upload', command: () => handleUploadScoreReportClick('STAR', 'STAR Diagnostic Report Early Literacy') },
                      { label: 'i-Ready', subtitle: 'Coming soon', icon: 'file_upload', disabled: true },
                      { label: 'NWEA MAP', subtitle: 'Coming soon', icon: 'file_upload', disabled: true }
                    ]"
                  >
                    <template #buttonContents>
                      <span class="material-icons-outlined text-[24px] text-[#2c5f7a]">add</span>
                    </template>
                  </DropdownButton>
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold text-blue-900 text-lg">Upload Score Report</span>
                      <span class="text-white font-semibold tracking-[0.5px] uppercase bg-[#2c5f7a] text-[10px] px-2 py-0.5 rounded-md">BETA</span>
                    </div>
                    <p class="text-md text-blue-800 mt-0.5">STAR Early Literacy & STAR Reading · More assessment support coming soon</p>
                  </div>
                </div>
                <!-- Standards (all subjects) -->
                <div v-if="Object.keys(groupedStandards).length" data-testid="inner-bottom-content" class="text-sm my-1">
                  <div v-for="(standardGroup, setCode) in groupedStandards" :key="setCode">
                    {{ headingsForStandardSetCode(setCode as string, { profileId: contentProfileStore.activeProfile?.id }) || standardGroup[0].standard_set }}:
                    <span v-for="standard in standardGroup" :key="standard.standard_long_ref">
                      <StandardPopover :standard="standard" /><span v-if="standard !== standardGroup[standardGroup.length - 1]">, </span>
                    </span>
                  </div>
                </div>
                <!-- Category chips (all subjects) -->
                <div v-if="!(props.savedSummary && !visibilityStore.aiFeatureVisibility)" class="flex flex-wrap gap-x-2 gap-y-2 mt-2">
                  <GbButton
                    v-for="categoryOption in categoryOptions"
                    :key="categoryOption.uuid"
                    :id="`category-button-${categoryOption.uuid}`"
                    :label="categoryOption.label"
                    gbTheme="grayOutlineSecondary"
                    size="small"
                    @click="setSelectedCategory(categoryOption)">
                    <span class="material-icons-round mr-1 text-green-300 text-base">add_circle</span>{{ categoryOption.label }}
                  </GbButton>
                </div>
              </template>
              <template #innerBottomContent>
              </template>
              <template #bottomLeftContent>
                <span v-if="saveStatus === PL_SUMMARY_SAVING_STATES.SAVING" class="material-icons-round text-lg animate-spin">autorenew</span>
              </template>
              <template #bottomRightContent>
              </template>
            </WikiTextEditor>
          </CopyTrackWrapper>

          <div v-if="['Reading', 'Writing', 'Math'].includes(props.summary?.subject_label || '') && !(props.savedSummary && !visibilityStore.aiFeatureVisibility)" class="px-5 py-[18px] border-t border-gray-300">
            <CopyTrackWrapper
              type="impact_statement"
              :title="impactStatementCopyTitle"
            >
              <PresentlevelsImpactStatement
                :subject-label="props.summary?.subject_label || ''"
                :student-name="props.summary?.student_name || ''"
                :impact-statements="impactStatements"
                @add="handleAddImpactStatement"
                @remove="handleRemoveImpactStatement"
                @update="handleUpdateImpactStatement"
              />
            </CopyTrackWrapper>
          </div>
        </div>
    </div>
    <SubjectSummaryLoading v-else />

    <GbDialog
      dialog-id="delete-statement-modal"
      :visible="showDeleteModal"
      @update:visible="showDeleteModal = $event"
      header="Delete Statement"
      :footer-buttons="true"
    >
      <template #bodyContent>
        <p>You have created content for this <span class="font-bold">{{ props.summary?.subject_label?.toUpperCase() }}</span> statement. If you proceed, all content in this statement will be lost. Are you sure you want to delete this statement?</p>
      </template>
      <template #footerButtonLeft>
        <GbButton
          type="button"
          gbTheme="grayOutlineSecondary"
          label="Cancel"
          @click="showDeleteModal = false">
        </GbButton>
      </template>
      <template #footerButtonRight>
        <GbButton
          type="button"
          gbTheme="redPrimary"
          label="Delete Statement"
          @click="deleteStatement">
        </GbButton>
      </template>
    </GbDialog>

    <GbDialog
      dialog-id="clear-statement-modal"
      :visible="clearModalType !== null"
      @update:visible="(val) => { if (!val) clearModalType = null }"
      :header="clearModalConfig.header"
      :footer-buttons="true"
    >
      <template #bodyContent>
        <p v-html="clearModalConfig.body"></p>
      </template>
      <template #footerButtonLeft>
        <GbButton
          type="button"
          gbTheme="grayOutlineSecondary"
          label="Cancel"
          @click="clearModalType = null">
        </GbButton>
      </template>
      <template #footerButtonRight>
        <GbButton
          type="button"
          gbTheme="redPrimary"
          label="Clear Statement"
          @click="confirmClear">
        </GbButton>
      </template>
    </GbDialog>

    <PresentlevelsSkillsModal
      :key="selectedCategory?.uuid"
      :summary-uuid="props.summary?.uuid || ''"
      :category-label="selectedCategory?.label"
      :category-uuid="selectedCategory?.uuid || ''"
      :subject-label="props.summary?.subject_label || ''"
      :skill-level-groups="selectedCategory?.level_groups"
      @modal-closed="setSelectedCategory(null)"
      :disable-autosave="props.savedSummary"
      :statement-text="statementText"
      :aligned-goals="props.summary?.aligned_goals"
      :browse-assessment-domain="selectedCategory?.browse_assessment_domain || ''"
      :browse-assessment-subject="selectedCategory?.browse_assessment_subject || ''"
      :browse-assessment-type="selectedCategory?.browse_assessment_type || ''"
      :is-library="props.savedSummary"
    />
    <AssessmentUploadModal
      :visible="showAssessmentModal"
      :score-report-series="selectedScoreReportSeries"
      :score-report-title="selectedScoreReportTitle"
      @update:visible="showAssessmentModal = $event"
      @statement-inserted="handleStatementInserted"
    />
    <slot />
  </div>
  <CopySummaryModal
    v-if="showCopySummaryModal"
    :content-uuid="summary?.uuid"
    :preserve-session="true"
    :post-copy-navigate="false"
    @save="() => $emit('copied')"
    @update:visible="(isVisible: boolean) => showCopySummaryModal = isVisible"
  />
</template>

<style>
/* Remove the tiptap's own bottom border and rounded corners for the Reading editor,
   so the Upload Score Report footer connects seamlessly to the text area */
.reading-plw-editor .tiptap {
  border-bottom-color: transparent;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
</style>